import React from 'react';
import { useGameStore } from '../../state/gameState.js';
import { getBioscan, BIOSCAN_OBSERVATIONS } from '../../systems/bioscan.js';
import { getEnvScan } from '../../systems/envscan.js';
import { getEvidence } from '../../content/evidenceCanon.js';
import { EvidenceImage, EvidenceStyles } from '../EvidenceBoard.jsx';
import CaseNotes from './CaseNotes.jsx';

// De-slug a scene id into a readable origin (compact left-panel cue).
function humanizeScene(id) {
  return String(id || '—').replace(/^ds_/, '').replace(/_/g, ' ');
}

/**
 * EVD FOUND — the compact discovery card shown in the narrow left panel when a
 * piece of evidence is examined (real store activeEvidenceDossier). Replaces the
 * old full-screen EvidenceDossier overlay; the full record (description, cross-
 * refs, present-to-Hargrove) lives in the in-frame EVD LOCKER glass panel.
 */
function EvdFound({ item, onClose, onOpenLocker }) {
  return (
    <>
      <EvidenceStyles />
      <div className="lp-hdr am">EVD FOUND</div>
      <div className="lp-b">
        <div className="levdt">{item.title}</div>
        <EvidenceImage item={item} className="ds-ev-card-img" onClick={onOpenLocker} />
        <div className="lmicro mut">origin</div>
        <div className="levdmeta">{humanizeScene(item.sceneId)}</div>
        <div className="lmicro mut">class</div>
        <div className="levdmeta">{item.corruptible ? '▓▓ RESTRICTED' : 'UNRESTRICTED'}</div>
        <button type="button" className="lsave" onClick={onOpenLocker}>{'◈ OPEN\nLOCKER'}</button>
        <button type="button" className="lsave" onClick={onClose}>{'✕ CLOSE'}</button>
      </div>
    </>
  );
}

/**
 * HUD left instrument panel (v3 `.lp`).
 *
 * During an active Hargrove interview it renders the LIVE cyan BIO-SCAN readout
 * (keyed to the store's interview topic via getBioscan, with a ▲/▼ trend vs the
 * previous heart rate and the ▓▓ classification ▓▓ gate block). Otherwise it
 * renders the static ENV-SCAN. Mirrors DeadSignalHUD's CompactBioscan look.
 */
function BioScan({ bio, prevHR, onBioscanClick }) {
  const [corrupted, setCorrupted] = React.useState(null);
  const hr = bio.heartRate;
  const d = hr - prevHR;
  const cls = d > 10 ? 'red' : d > 5 ? 'amb' : '';
  const trend = d > 5 ? '▲' : d < -5 ? '▼' : '—';
  const word = d > 10 ? 'SPIKE' : d > 5 ? 'elevated' : d < -5 ? 'dropping' : 'stable';
  const gate = bio.classification;
  // Clicking a metric transmits an observation about it through the interference
  // gate (same contract as the in-scene HargroveBioscanHUD: the real
  // BIOSCAN_OBSERVATIONS generator -> onBioscanClick -> {suppressed} verdict).
  const click = (key) => {
    if (!onBioscanClick) return;
    const gen = BIOSCAN_OBSERVATIONS[key];
    if (!gen) return;
    const result = onBioscanClick(gen(bio));
    if (result?.suppressed) {
      setCorrupted(key);
      setTimeout(() => setCorrupted(null), 600);
    }
  };
  const hot = onBioscanClick ? { cursor: 'pointer' } : undefined;
  const cor = (key) => (corrupted === key ? ' corrupted' : '');
  return (
    <>
      <div className="lp-hdr cy">BIO-SCAN</div>
      <div className="lp-b">
        <div className={'lbig' + (cls ? ' ' + cls : '') + cor('heartRate')} style={hot} onClick={() => click('heartRate')} title={onBioscanClick ? 'Transmit observation' : undefined}>{hr}<span style={{ fontSize: '10px' }}> bpm</span></div>
        <div className={'lbig' + (cls ? ' ' + cls : '')} style={{ fontSize: '11px' }}>{trend} {word}</div>
        {d !== 0 && <div className="ldelta">{prevHR} &rarr; {hr}</div>}
        <hr className="lrule cy" />
        <div className={'lr' + cor('skinTemp')} style={hot} onClick={() => click('skinTemp')}><span className="ll">temp</span><span className="lcy">{bio.skinTempF}&deg;F</span></div>
        <div className="lmicro">persp</div>
        <div className={'lcy' + cor('perspiration')} style={{ ...hot, marginBottom: 3 }} onClick={() => click('perspiration')}>{bio.perspiration}</div>
        <div className="lmicro">micro-expr</div>
        <div className={'lcy' + cor('microExpr')} style={{ ...hot, lineHeight: 1.3, marginBottom: 3 }} onClick={() => click('microExpr')}>{bio.microExpr}</div>
        <hr className="lrule cy" />
        <div className="lmicro" style={{ marginBottom: 2 }}>ASSESS</div>
        <div className="lassess">{bio.assessment}</div>
        {gate && <div className="lgate">{'▓▓ ' + gate.level + ' ▓▓\n' + gate.bucket}</div>}
      </div>
    </>
  );
}

// Chip instrumentation readout for the current scene: quantities Craine
// cannot sense unaided. Values come from envscan.js (canon source:
// scene-bible.json environment blocks).
function EnvScan({ sceneId, scanMode, onToggleScan }) {
  const env = getEnvScan(sceneId);
  return (
    <>
      <div className="lp-hdr">ENV-SCAN</div>
      <div className="lp-b">
        {env.rows.map(([label, value]) => (
          <div className="lr" key={label}><span className="ll">{label}</span><span className="lv">{value}</span></div>
        ))}
        {env.anomaly && <div className="lenv-note">{env.anomaly}</div>}
        {onToggleScan && (
          <button
            type="button"
            className={'lscan' + (scanMode ? ' active' : '')}
            onClick={onToggleScan}
            aria-pressed={!!scanMode}
            title="Reveal interactable points in this scene"
          >
            {scanMode ? '◈ SCAN\nACTIVE' : '◈ SCAN\nSCENE'}
          </button>
        )}
      </div>
    </>
  );
}

/**
 * LUMEN instrument readout display. Hotspot examinations and READOUT commands
 * land HERE (store.activeReadout), never as transcript dialogue lines, so the
 * instrument block and Craine's spoken reaction stop colliding in the
 * terminal (owner ruling 2026-06-12). The data renders verbatim.
 */
function Readout({ readout, onClose }) {
  return (
    <>
      <div className="lp-hdr am">{readout.type || 'READOUT'}</div>
      <div className="lp-b">
        <pre className="lro">{readout.data}</pre>
        <button type="button" className="lsave" onClick={onClose}>{'✕ CLOSE'}</button>
      </div>
    </>
  );
}

export default function HudLeftPanel({ scanMode, onToggleScan, onBioscanClick, dossierId, onDossierClose, onOpenLocker }) {
  const interviewActive = useGameStore((s) => s.interview.active);
  const sceneId = useGameStore((s) => s.sceneId);
  const topic = useGameStore((s) => s.interview.topic);
  const prevHR = useGameStore((s) => s.interview._prevHeartRate ?? 74);
  const activeReadout = useGameStore((s) => s.activeReadout);
  const clearReadout = useGameStore((s) => s.clearReadout);

  // Priority: an active interview keeps the live BIO-SCAN; otherwise a freshly
  // examined piece of evidence shows its EVD FOUND card; otherwise the active
  // instrument readout; otherwise ENV-SCAN.
  const dossierItem = !interviewActive && dossierId ? getEvidence(dossierId) : null;

  return (
    <div className="lp">
      {interviewActive
        ? <BioScan bio={getBioscan(topic)} prevHR={prevHR} onBioscanClick={onBioscanClick} />
        : dossierItem
          ? <EvdFound item={dossierItem} onClose={onDossierClose} onOpenLocker={onOpenLocker} />
          : activeReadout
            ? <Readout readout={activeReadout} onClose={clearReadout} />
            : <EnvScan sceneId={sceneId} scanMode={scanMode} onToggleScan={onToggleScan} />}
      <CaseNotes />
    </div>
  );
}
